import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import style from './CareerCompanion.module.css';
import MapExplorer from './MapExplorer';
import Cookies from 'js-cookie';
import Spinner from './Spinner';
// Simple SVG Circular Progress Component
const CircularProgress = ({ value, size = 120, strokeWidth = 10, color = '#432C5B' }) => {
    const radius = (size - strokeWidth) / 2;
    const circumference = radius * 2 * Math.PI;
    const offset = circumference - (value / 100) * circumference;

    return (
        <div style={{ position: 'relative', width: size, height: size, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke="#E6E6E6"
                    strokeWidth={strokeWidth}
                    fill="none"
                />
                <circle
                    cx={size / 2}
                    cy={size / 2}
                    r={radius}
                    stroke={color}
                    strokeWidth={strokeWidth}
                    fill="none"
                    strokeDasharray={circumference}
                    strokeDashoffset={offset}
                    strokeLinecap="round"
                    style={{ transition: 'stroke-dashoffset 1s ease-out' }}
                />
            </svg>
            <div style={{ position: 'absolute', fontSize: '24px', fontWeight: 'bold', color: color }}>
                {value ?? 0}
            </div>
        </div>
    );
};

// Decode user_id from a JWT string without verifying the signature.
// Safe to use client-sidewe only need the id to display UI, not for auth.
const getUserIdFromToken = () => {
    const token = Cookies.get('token');
    if (!token) return null;
    try {
        const b64 = token.split('.')[1].replace(/-/g, '+').replace(/_/g, '/');
        const payload = JSON.parse(atob(b64));
        return payload.user_id || null;
    } catch {
        return null;
    }
};

const CareerCompanionPage = () => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();
    // Prefer explicit ?id= param; fall back to the logged-in user's id from JWT
    const userId = searchParams.get('id') || getUserIdFromToken();
    const [selectedTab, setSelectedTab] = useState('Overview');

    // Data State
    const [analysis, setAnalysis] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    // Gate: user must click "Generate Analysis" before any backend call
    const [hasStarted, setHasStarted] = useState(false);

    // Job search state (lifted so it persists across tab changes)
    const [selectedJobRole, setSelectedJobRole] = useState(null);
    const [jobLocation, setJobLocation] = useState('San Francisco, CA');
    const [jobCompanies, setJobCompanies] = useState([]);

    const API_URL = process.env.REACT_APP_CAREER_ANALYSIS_API_URL || "https://chimpknows.com";

    useEffect(() => {
        if (!hasStarted) return;

        if (!userId || userId === 'guest' || userId === 'current') {
            setLoading(false);
            return;
        }

        // Check for cached data first
        const cacheKey = `cc_analysis_${userId}`;
        const cachedData = Cookies.get(cacheKey);
        if (cachedData) {
            try {
                const parsed = JSON.parse(cachedData);
                setAnalysis(parsed);
                setError(null);
                setLoading(false);
                return;
            } catch (e) {
                console.error("Failed to parse cached data:", e);
            }
        }

        setLoading(true);

        const token = Cookies.get('token');

        fetch(`${API_URL}/analyze`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify({ user_id: userId })
        })
            .then(async response => {
                if (!response.ok) {
                    const text = await response.text();
                    let detail = '';
                    try {
                        const parsed = JSON.parse(text);
                        detail = parsed.detail || '';
                    } catch {
                        detail = text;
                    }

                    if (
                        response.status === 404 &&
                        typeof detail === 'string' &&
                        detail.startsWith('No file found in master.files')
                    ) {
                        navigate('/dashboard/upload');
                        throw new Error('Redirecting to upload page');
                    }

                    throw new Error(detail || 'Failed to fetch analysis');
                }
                return response.json();
            })
            .then(data => {
                setAnalysis(data);
                setError(null);
                Cookies.set(`cc_analysis_${userId}`, JSON.stringify(data), { expires: 7 });
            })
            .catch(err => {
                if (err.message === 'Redirecting to upload page') return;
                console.error("Fetch Error:", err);
                setError(err.message);
            })
            .finally(() => setLoading(false));
    }, [hasStarted, userId, API_URL, navigate]);

    // When analysis arrives, set default role & location (only once)
    useEffect(() => {
        if (!analysis) return;

        // default role: first suggested profile
        if (!selectedJobRole && analysis.suggested_profiles && analysis.suggested_profiles.length > 0) {
            setSelectedJobRole(analysis.suggested_profiles[0]);
        }

        // default location: candidate_location if present
        if (analysis.candidate_location && (!jobLocation || jobLocation === 'San Francisco, CA')) {
            setJobLocation(analysis.candidate_location);
        }
    }, [analysis]); // intentionally not depending on selectedJobRole/jobLocation to avoid re-running

    const handleUseMock = () => {
        const mock = {
            score: 85,
            candidate_location: "New York, NY",
            skills: ["React", "Python", "Data Analysis", "UI/UX"],
            interests: ["Artificial Intelligence", "Web Development"],
            summary: "Experienced developer with a strong background in frontend technologies and a growing interest in AI integrations.",
            suggested_profiles: ["Frontend Engineer", "Full Stack Developer"],
            detailed_metrics: {
                "Technical Proficiency": 90,
                "Soft Skills": 80,
                "Experience Impact": 85,
                "Education/Certifications": 75,
                "Presentation": 88
            },
            agentic_advice: [
                "Consider contributing to open source AI projects to strengthen your portfolio.",
                "Your React skills are strong; focus on Next.js for full-stack capabilities.",
                "Highlight concrete achievements (metrics, outcomes) under each role."
            ]
        };
        setAnalysis(mock);
        setSelectedJobRole(mock.suggested_profiles[0]);
        setJobLocation(mock.candidate_location);
        setError(null);
    };

    // ------------------- LANDING SCREEN -------------------

    const renderLanding = () => (
        <div className={style.landingScreen}>
            <div className={style.landingIconWrap}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M14 2H6C5.46957 2 4.96086 2.21071 4.58579 2.58579C4.21071 2.96086 4 3.46957 4 4V20C4 20.5304 4.21071 21.0391 4.58579 21.4142C4.96086 21.7893 5.46957 22 6 22H18C18.5304 22 19.0391 21.7893 19.4142 21.4142C19.7893 21.0391 20 20.5304 20 20V8L14 2Z" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M14 2V8H20" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 13H8" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M16 17H8" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                    <path d="M10 9H9H8" stroke="#432C5B" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
            </div>
            <h1 className={style.landingTitle}>CV Analyzer</h1>
            <p className={style.landingSubtitle}>
                Get an AI-powered breakdown of your CV skills, strengths, improvement areas, and role recommendations tailored to you.
            </p>
            {userId && userId !== 'guest' && userId !== 'current' ? (
                <>
                    <button
                        className={style.generateButton}
                        onClick={() => setHasStarted(true)}
                    >
                        Generate Analysis
                        <span className={style.generateButtonArrow}>→</span>
                    </button>
                    <p className={style.landingNote}>Analysis typically takes 15–30 seconds</p>
                </>
            ) : (
                <p className={style.landingNoUser}>
                    No profile linked. Please navigate here from your dashboard.
                </p>
            )}
        </div>
    );

    // ------------------- RENDER SECTIONS -------------------

    const renderOverview = () => (
        <div className={style.section}>
            {loading && <Spinner size="large" message="Analyzing your CV..." />}

            {error && (
                <div className={style.alert}>
                    <strong>Backend Connection Failed</strong>
                    <p style={{ fontSize: '0.9em', margin: '5px 0' }}>
                        {error}. <br />
                        Ensure the backend is running.
                    </p>
                    <button
                        onClick={handleUseMock}
                        className={style.button}
                        style={{ marginTop: '10px', fontSize: '12px', padding: '5px 10px' }}
                    >
                        Use Demo Data
                    </button>
                </div>
            )}

            {!loading && !error && analysis && (
                <>
                    {/* High-level stats */}
                    <div className={style.placeholderGrid}>
                        {/* CV Score with Circular Progress */}
                        <div className={style.metricCard} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                            <h3 style={{ marginBottom: '15px' }}>CV Score</h3>
                            <CircularProgress value={analysis.score || 0} />
                            <p style={{ fontSize: '0.8rem', marginTop: '10px', color: '#65677d' }}>
                                Overall strength
                            </p>
                        </div>

                        {/* Top Skill with Badge Icon */}
                        <div className={style.metricCard} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <h3>Top Skill</h3>
                            <div className={style.iconCircle}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 15L8.5 17L9.5 13L6.5 10.5L10.5 10L12 6L13.5 10L17.5 10.5L14.5 13L15.5 17L12 15Z" stroke="#432C5B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                </svg>
                            </div>
                            <div className={style.bigNumber} style={{ fontSize: '24px' }}>
                                {analysis.skills && analysis.skills.length > 0 ? analysis.skills[0] : '--'}
                            </div>
                            <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>Prominent Skill</p>
                        </div>

                        {/* Location with Pin Icon */}
                        <div className={style.metricCard} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
                            <h3>Location</h3>
                            <div className={style.iconCircle} style={{ background: 'rgba(255, 120, 120, 0.1)' }}>
                                <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M12 21C16 17 20 13 20 9C20 4.58172 16.4183 1 12 1C7.58172 1 4 4.58172 4 9C4 13 8 17 12 21Z" stroke="#E65A5A" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                                    <circle cx="12" cy="9" r="3" stroke="#E65A5A" strokeWidth="2" />
                                </svg>
                            </div>
                            <div className={style.subHeader} style={{ fontSize: '20px', fontWeight: '600', color: '#333' }}>
                                {analysis.candidate_location || 'Not detected'}
                            </div>
                            <p style={{ fontSize: '0.8rem', marginTop: '4px' }}>Target Region</p>
                        </div>
                    </div>

                    {/* Summary + suggested profiles */}
                    <div
                        className={style.metricCard}
                        style={{ marginTop: '20px', textAlign: 'left' }}
                    >
                        <h3>Profile Summary</h3>
                        <p>{analysis.summary}</p>

                        {analysis.suggested_profiles && analysis.suggested_profiles.length > 0 && (
                            <>
                                <h4 style={{ marginTop: '15px' }}>Suggested Roles</h4>
                                <ul className={style.adviceList}>
                                    {analysis.suggested_profiles.map((role, idx) => (
                                        <li key={idx}>{role}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>

                    {/* Key skills + quick advice */}
                    <div
                        className={style.metricCard}
                        style={{ marginTop: '20px', textAlign: 'left' }}
                    >
                        <h3>Key Skills Snapshot</h3>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                            {analysis.skills?.slice(0, 10).map((skill, idx) => (
                                <span
                                    key={idx}
                                    style={{
                                        padding: '4px 10px',
                                        borderRadius: '999px',
                                        border: '1px solid rgba(255,255,255,0.2)',
                                        fontSize: '0.8rem'
                                    }}
                                >
                                    {skill}
                                </span>
                            ))}
                        </div>

                        {analysis.agentic_advice && analysis.agentic_advice.length > 0 && (
                            <>
                                <h4 style={{ marginTop: '15px' }}>Quick Wins</h4>
                                <ul className={style.adviceList}>
                                    {analysis.agentic_advice.slice(0, 3).map((advice, idx) => (
                                        <li key={idx}>{advice}</li>
                                    ))}
                                </ul>
                            </>
                        )}
                    </div>
                </>
            )}
        </div>
    );

    const renderAnalysis = () => {
        if (!analysis) {
            return <p>No analysis data available yet. Upload your CV or use demo data.</p>;
        }

        return (
            <div className={style.section}>
                <h2>Skill & CV Analysis</h2>
                <p style={{ marginBottom: '16px' }}>
                    Here’s how your CV performs across different dimensions and what strengths stand out.
                </p>

                {/* Metric bars */}
                <div className={style.grid}>
                    {analysis.detailed_metrics &&
                        Object.entries(analysis.detailed_metrics).map(([key, value]) => (
                            <div key={key} className={style.metricCard}>
                                <h3>{key}</h3>
                                <div className={style.progressBarContainer}>
                                    <div
                                        className={style.progressBarFill}
                                        style={{ width: `${value}%` }}
                                    ></div>
                                </div>
                                <p>{value}%</p>
                            </div>
                        ))}
                </div>

                {/* Skills */}
                <h3 style={{ marginTop: '24px' }}>Skills Detected</h3>
                <p style={{ fontSize: '0.9rem' }}>
                    These are the skills the AI picked up from your CV. Make sure the most important ones for your target roles are clearly highlighted.
                </p>
                <div
                    style={{
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: '8px',
                        marginTop: '10px',
                        marginBottom: '20px'
                    }}
                >
                    {analysis.skills?.map((skill, idx) => (
                        <span
                            key={idx}
                            style={{
                                padding: '4px 10px',
                                borderRadius: '999px',
                                border: '1px solid rgba(255,255,255,0.2)',
                                fontSize: '0.8rem'
                            }}
                        >
                            {skill}
                        </span>
                    ))}
                </div>

                {/* Advice */}
                <h3>Agentic Advice</h3>
                <ul className={style.adviceList}>
                    {analysis.agentic_advice?.map((advice, i) => (
                        <li key={i}>{advice}</li>
                    ))}
                </ul>
            </div>
        );
    };

    return (
        <div className={style.pageContainer}>
            <div className={style.glassCard}>
                {!hasStarted ? (
                    renderLanding()
                ) : (
                    <>
                        <div className={style.navTabs}>
                            {['Overview', 'Analysis', 'Find Jobs Near You'].map(tab => (
                                <button
                                    key={tab}
                                    className={`${style.tabButton} ${selectedTab === tab ? style.activeTab : ''}`}
                                    onClick={() => setSelectedTab(tab)}
                                >
                                    {tab}
                                </button>
                            ))}
                        </div>

                        <div className={style.contentArea}>
                            {selectedTab === 'Overview' && renderOverview()}
                            {selectedTab === 'Analysis' && renderAnalysis()}
                            {selectedTab === 'Find Jobs Near You' && (
                                <MapExplorer
                                    jobProfile={selectedJobRole}
                                    setJobProfile={setSelectedJobRole}
                                    availableRoles={analysis?.suggested_profiles || []}
                                    location={jobLocation}
                                    setLocation={setJobLocation}
                                    companies={jobCompanies}
                                    setCompanies={setJobCompanies}
                                    apiUrl={API_URL}
                                />
                            )}
                        </div>
                    </>
                )}
            </div>
        </div>
    );
};

export default CareerCompanionPage;
